import { all, call, fork, take, takeEvery } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import { getMasqueradeUser } from '../../utilities';
import {
  getProfileAsync,
  logoutUser,
  updateProfileAsync,
} from '../../actions/user';
import * as U from '../../clients/mira/types/User';
import miraClient from '../../clients/miraClient';
import analytics from '../../clients/analyticsClient';

function identify(profile: U.Profile) {
  // Don't identify the user when masquerading.
  if (getMasqueradeUser()) return;

  // Link Amplitude events (e.g. user_login) to the profile so they join the
  // backend events Segment sends keyed by the same profile ID.
  analytics.identifyUser(profile.id);

  if (!window.analytics || typeof window.analytics.identify !== 'function')
    return;
  window.analytics.identify(profile.id, {
    domain_id: profile.domainId || null,
  });
}

function* trackUserLoginIfNeeded() {
  const justLoggedIn = sessionStorage.getItem('just_logged_in');
  if (!justLoggedIn) return;
  sessionStorage.removeItem('just_logged_in');

  try {
    const onboardingData: { length: number } = yield call(() =>
      miraClient.getOnboardingStatus(),
    );
    analytics.trackUserLogin({ firstLogin: onboardingData.length === 0 });
  } catch {
    // If onboarding fetch fails, still track login without first_login info
    analytics.trackUserLogin({ firstLogin: false });
  }
}

function* identifyInitial() {
  // identify the user only once after page load or login
  const { payload: profile }: { payload: U.Profile } = yield take(
    getType(getProfileAsync.success),
  );
  yield call(identify, profile);
  yield call(trackUserLoginIfNeeded);
}

function* identifyOnUpdate() {
  // re-identify the user after they update their details
  yield takeEvery(
    getType(updateProfileAsync.success),
    function* ({
      payload: user,
    }: ReturnType<typeof updateProfileAsync.success>) {
      yield call(identify, user);
    },
  );
}

function* reidentifyAfterLogout() {
  yield takeEvery(getType(logoutUser), function* () {
    // Clear the previous user's Amplitude identity, then re-arm identify for
    // the next login.
    analytics.resetUser();
    yield call(identifyInitial);
  });
}

export default all([
  fork(identifyInitial),
  fork(identifyOnUpdate),
  fork(reidentifyAfterLogout),
]);
